import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  History, 
  RefreshCw, 
  ScanBarcode, 
  Search,
  Filter,
  ChevronRight,
  ChevronLeft,
  Clock,
  Package,
  CheckCircle,
  XCircle,
  Calendar,
  Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import RetailerLayout from '../RetailerLayout';

/**
 * ScanHistoryPage - Full scan history with filters, search, and pagination
 * 
 * Features:
 * - Search by barcode or product name
 * - Filter by date range, posted status, category
 * - Pagination with configurable page size
 * - Export to CSV
 * - Quick repost functionality
 */
const ScanHistoryPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // State
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [statusFilter, setStatusFilter] = useState('all'); // all, posted, not_posted
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [certificationFilter, setCertificationFilter] = useState('all'); // all, halal, kosher, organic, vegan, gluten_free
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Categories for filter
  const [categories, setCategories] = useState([]);

  // Fetch scans with filters
  const fetchScans = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String((page - 1) * pageSize)
      });
      
      // Add filters
      if (statusFilter === 'posted') {
        params.append('rshd_only', 'true');
      }
      
      // Use search endpoint if query provided
      if (searchQuery.trim()) {
        const response = await api.get(`/barcode/history/search?q=${encodeURIComponent(searchQuery)}&limit=${pageSize}`);
        if (response.data.success) {
          setScans(response.data.products || []);
          setTotalCount(response.data.count || 0);
        }
      } else {
        const response = await api.get(`/barcode/history?${params.toString()}`);
        if (response.data.success) {
          let filteredScans = response.data.scans || [];
          
          // Client-side date filtering
          if (dateFilter !== 'all') {
            const now = new Date();
            filteredScans = filteredScans.filter(scan => {
              const scanDate = new Date(scan.scanned_at);
              const diffDays = Math.floor((now - scanDate) / (1000 * 60 * 60 * 24));
              
              if (dateFilter === 'today') return diffDays === 0;
              if (dateFilter === 'week') return diffDays <= 7;
              if (dateFilter === 'month') return diffDays <= 30;
              return true;
            });
          }
          
          // Client-side category filtering
          if (categoryFilter !== 'all') {
            filteredScans = filteredScans.filter(scan => 
              scan.category === categoryFilter
            );
          }
          
          // Client-side certification filtering
          if (certificationFilter !== 'all') {
            filteredScans = filteredScans.filter(scan => {
              if (certificationFilter === 'halal') return scan.is_halal === true;
              if (certificationFilter === 'kosher') return scan.is_kosher === true;
              if (certificationFilter === 'organic') return scan.is_organic === true;
              if (certificationFilter === 'vegan') return scan.is_vegan === true;
              if (certificationFilter === 'gluten_free') return scan.is_gluten_free === true;
              return true;
            });
          }
          
          setScans(filteredScans);
          setTotalCount(response.data.count || filteredScans.length);
        }
      }
    } catch (error) {
      console.error('Failed to fetch scan history:', error);
      setScans([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, dateFilter, statusFilter, categoryFilter, certificationFilter]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories');
        if (response.data) {
          setCategories(response.data.map(c => c.name || c));
        }
      } catch (e) {
        console.error('Failed to fetch categories:', e);
      }
    };
    fetchCategories();
  }, []);

  // Fetch scans when filters change
  useEffect(() => {
    fetchScans();
  }, [fetchScans]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, dateFilter, statusFilter, categoryFilter, certificationFilter]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchScans();
  };

  // Handle repost
  const handleRepost = (scan) => {
    navigate('/retailer/scan', { 
      state: { 
        repostData: {
          raw_code: scan.raw_code,
          format: scan.format,
          product_name: scan.product_name,
          category: scan.category
        }
      } 
    });
  };

  // Export to CSV
  const handleExport = () => {
    const csvContent = [
      ['Barcode', 'Product Name', 'Category', 'Format', 'Scanned At', 'Posted'].join(','),
      ...scans.map(scan => [
        scan.raw_code,
        `"${(scan.product_name || '').replace(/"/g, '""')}"`,
        scan.category || '',
        scan.format || '',
        scan.scanned_at || '',
        scan.created_rshd ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scan-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Pagination
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <RetailerLayout user={user} onLogout={onLogout}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <History className="w-6 h-6 text-blue-600" />
              Scan History
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              View and manage your barcode scan history
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
            disabled={scans.length === 0}
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchScans}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by barcode or product name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </form>
            
            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-blue-50 border-blue-300' : ''}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-1" />
              Filters
              {(dateFilter !== 'all' || statusFilter !== 'all' || categoryFilter !== 'all' || certificationFilter !== 'all') && (
                <Badge className="ml-2 bg-blue-500">Active</Badge>
              )}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4 pt-4 border-t">
              {/* Date Filter */}
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Date Range</label>
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger data-testid="date-filter">
                    <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">Last 30 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Posted Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Scans</SelectItem>
                    <SelectItem value="posted">Posted Only</SelectItem>
                    <SelectItem value="not_posted">Not Posted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Category</label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger data-testid="category-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Certification Filter */}
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Certification</label>
                <Select value={certificationFilter} onValueChange={setCertificationFilter}>
                  <SelectTrigger data-testid="certification-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Certifications</SelectItem>
                    <SelectItem value="halal">✅ Halal</SelectItem>
                    <SelectItem value="kosher">✅ Kosher</SelectItem>
                    <SelectItem value="organic">🌿 Organic</SelectItem>
                    <SelectItem value="vegan">🌱 Vegan</SelectItem>
                    <SelectItem value="gluten_free">GF Gluten-Free</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Page Size */}
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Items per page</label>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v))}>
                  <SelectTrigger data-testid="page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters */}
              <div className="md:col-span-5 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDateFilter('all');
                    setStatusFilter('all');
                    setCategoryFilter('all');
                    setCertificationFilter('all');
                    setSearchQuery('');
                  }}
                  data-testid="clear-filters-btn"
                >
                  Clear All Filters
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {loading ? 'Loading...' : `${totalCount} scans found`}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-8 h-8 text-gray-300 animate-spin mx-auto mb-2" />
              <p className="text-gray-500">Loading scan history...</p>
            </div>
          ) : scans.length === 0 ? (
            <div className="py-8 text-center">
              <ScanBarcode className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-2">No scans found</p>
              <p className="text-sm text-gray-400">
                {searchQuery ? 'Try a different search term' : 'Start scanning to build your history'}
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate('/retailer/scan')}
              >
                <ScanBarcode className="w-4 h-4 mr-2" />
                Go to Scanner
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {scans.map((scan, index) => (
                <div 
                  key={`${scan.raw_code}-${scan.scanned_at}-${index}`}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors group"
                  data-testid={`scan-row-${index}`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Barcode & Format */}
                    <div className="flex flex-col">
                      <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                        {scan.raw_code}
                      </code>
                      <Badge variant="outline" className="text-[10px] mt-1 w-fit">
                        {scan.format || 'Unknown'}
                      </Badge>
                    </div>
                    
                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {scan.product_name || 'Unknown Product'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                        {scan.category && (
                          <span className="flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            {scan.category}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimestamp(scan.scanned_at)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Status */}
                    <div className="hidden md:flex items-center">
                      {scan.created_rshd ? (
                        <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Posted
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500 flex items-center gap-1">
                          <XCircle className="w-3 h-3" />
                          Not Posted
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-2 opacity-70 hover:opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                    onClick={() => handleRepost(scan)}
                    data-testid={`repost-btn-${index}`}
                  >
                    <ChevronRight className="w-4 h-4" />
                    Re-scan
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </RetailerLayout>
  );
};

export default ScanHistoryPage;
